import * as React from 'react'
import { Loader2 } from 'lucide-react'
import { BILLING } from '@lib/constants/billing'
import type { BillingPriceScheduleDto } from '@lib/dto/billing.dto'
import { formatPrice, parsePriceToMicros } from '@lib/helpers/money'
import { schedulePriceInputSchema, type SchedulePriceInput } from '@lib/input/billing.input'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Field, fieldMessageId } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { SegmentedControl } from '@/components/ui/segmented-control'
import { toast } from '@/components/ui/toast'
import { apiErrorMessage } from '@/helpers/apiErrorMessage'
import { localInputValue } from '@/helpers/datetime-local'
import { useSchedulePrice } from '@/hooks/useOpsPricing'

const NOTICE_MS = BILLING.priceIncreaseNoticeDays * 24 * 60 * 60_000

type When = 'now' | 'later'

/** Midnight, local time, a day past the notice an increase needs: a sensible "later". */
function defaultLater(now: Date): string {
  const day = new Date(now.getTime() + NOTICE_MS + 24 * 60 * 60_000)
  day.setHours(0, 0, 0, 0)
  return localInputValue(day)
}

interface Problems {
  price?: string
  when?: string
}

/**
 * What's wrong with a change before it's sent, or null: the server checks the
 * same rules, but these are the ones staff can fix in the form.
 */
function check(micros: number | null, when: When, later: string, latestMicros: number, now: Date): { input: SchedulePriceInput } | { problems: Problems } {
  if (micros === null) return { problems: { price: 'Enter a price in dollars, like 0.06.' } }
  if (!schedulePriceInputSchema.safeParse({ priceMicros: micros }).success) return { problems: { price: `Between ${formatPrice(1)} and ${formatPrice(BILLING.priceMaxMicros)}.` } }
  if (micros === latestMicros) return { problems: { price: `That’s already the price (${formatPrice(latestMicros)}).` } }
  const increase = micros > latestMicros
  const earliest = new Date(now.getTime() + NOTICE_MS)
  const tooSoon = `An increase needs at least ${String(BILLING.priceIncreaseNoticeDays)} days’ notice; pick a date on or after ${earliest.toLocaleString()}.`
  if (when === 'now') return increase ? { problems: { when: tooSoon } } : { input: { priceMicros: micros } }
  const at = later ? new Date(later) : null
  if (!at || Number.isNaN(at.getTime())) return { problems: { when: 'Pick when it takes effect.' } }
  if (at <= now) return { problems: { when: 'Pick a time in the future, or choose Now.' } }
  if (increase && at < earliest) return { problems: { when: tooSoon } }
  return { input: { priceMicros: micros, effectiveAt: at.toISOString() } }
}

interface SchedulePriceFormProps {
  schedule: BillingPriceScheduleDto
  onDone: () => void
}

/** Mounted each time the dialog opens, so it starts empty. */
function SchedulePriceForm({ schedule, onDone }: SchedulePriceFormProps) {
  const schedulePrice = useSchedulePrice()
  const latest = schedule.scheduled.at(-1) ?? schedule.current
  const [price, setPrice] = React.useState('')
  const [when, setWhen] = React.useState<When>('later')
  const [later, setLater] = React.useState(() => defaultLater(new Date()))
  const [problems, setProblems] = React.useState<Problems>({})
  const [formError, setFormError] = React.useState<string | null>(null)

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    const result = check(parsePriceToMicros(price), when, later, latest.priceMicros, new Date())
    if ('problems' in result) {
      setProblems(result.problems)
      return
    }
    setProblems({})
    setFormError(null)
    try {
      await schedulePrice.mutateAsync(result.input)
      const label = formatPrice(result.input.priceMicros)
      toast.success(
        result.input.effectiveAt
          ? `Scheduled ${label} per live envelope from ${new Date(result.input.effectiveAt).toLocaleString()}`
          : `The price is now ${label} per live envelope`,
      )
      onDone()
    } catch (failure) {
      setFormError(apiErrorMessage(failure, 'Could not schedule the change.'))
    }
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4">
      <DialogHeader>
        <DialogTitle>Schedule a price change</DialogTitle>
        <DialogDescription>
          For every billed organization, from the instant it takes effect, including credit they already hold. The
          last price scheduled is {formatPrice(latest.priceMicros)}.
        </DialogDescription>
      </DialogHeader>
      <Field label="New price" htmlFor="price-amount" error={problems.price ?? null} hint="Dollars per live envelope, like 0.06.">
        <Input
          id="price-amount"
          inputMode="decimal"
          value={price}
          aria-invalid={problems.price ? true : undefined}
          aria-describedby={fieldMessageId('price-amount')}
          onChange={(event) => setPrice(event.target.value)}
        />
      </Field>
      <Field
        label="Takes effect"
        htmlFor="price-when"
        error={problems.when ?? null}
        hint={`Only a decrease can take effect now. An increase needs ${String(BILLING.priceIncreaseNoticeDays)} days’ notice, and Owners are emailed when it’s scheduled.`}
      >
        <div className="space-y-2">
          <SegmentedControl
            id="price-when"
            ariaLabel="Takes effect"
            value={when}
            onChange={setWhen}
            options={[
              { value: 'now', label: 'Now' },
              { value: 'later', label: 'Later' },
            ]}
          />
          {when === 'later' ? (
            <Input
              type="datetime-local"
              aria-label="Takes effect at"
              value={later}
              aria-invalid={problems.when ? true : undefined}
              onChange={(event) => setLater(event.target.value)}
            />
          ) : null}
        </div>
      </Field>
      <p className="text-caption text-muted-foreground">Scheduled changes can’t be cancelled or edited.</p>
      {formError ? <p className="text-body text-danger">{formError}</p> : null}
      <DialogFooter>
        <Button type="button" variant="secondary" onClick={onDone}>
          Cancel
        </Button>
        <Button type="submit" disabled={schedulePrice.isPending}>
          {schedulePrice.isPending ? <Loader2 className="animate-spin" /> : null}
          Schedule
        </Button>
      </DialogFooter>
    </form>
  )
}

interface SchedulePriceDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  schedule: BillingPriceScheduleDto
}

/** Staff ADMIN adds a price change to the end of the schedule. */
export function SchedulePriceDialog({ open, onOpenChange, schedule }: SchedulePriceDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <SchedulePriceForm schedule={schedule} onDone={() => onOpenChange(false)} />
      </DialogContent>
    </Dialog>
  )
}
